// ========== Help content manifest ==========
//
// The ONE ordered HelpTab → HelpSection → HelpArticle tree. Article ids are
// global topic ids: `?help=<id>` opens the article in the side panel and
// /help/<tabId>/<id> is its anonymous page and /app/help/<tabId>/<id> is the
// signed-in equivalent. Ids are stable API — never rename a shipped one.
//
// The tree carries the complete current IA and every current slot is authored.
// A future unpublished entry may reserve an id and position, but it renders
// NOWHERE (no nav, no search, no "coming soon") until real content exists.
//
// Authoring rules:
//   - flagship walkthroughs may use one module; related workflow guides use a
//     domain catalog; calculator reference is generated from the live registry
//   - every claim describes shipped behavior (this is product truth, not
//     marketing) — update the article in the same PR that changes the feature
//   - run `npm run help:index` after editing (CI gates the committed index)

import type { HelpArticle, HelpSection, HelpTab } from "../types";
import { navigatingFirmarkArticle } from "./getting-started/navigating-firmark";
import { yourFirstMemberArticle } from "./getting-started/your-first-member";
import { calculationRegisterArticle } from "./calculations/calculation-register";
import { organizingCalculationsArticle } from "./calculations/organizing-calculations";
import { managingCalculationsArticle } from "./calculations/managing-calculations";
import { memberSheetsArticle } from "./calculations/member-sheets";
import { schemaSheetsArticle } from "./calculations/schema-sheets";
import { sheetLifecycleArticle } from "./calculations/sheet-lifecycle";
import { designMethodArticle } from "./calculations/design-method";
import { memberRolePresetsArticle } from "./calculations/member-role-presets";
import { memberGeometrySupportsArticle } from "./calculations/member-geometry-supports";
import { memberLoadsArticle } from "./calculations/member-loads";
import { designConditionsArticle } from "./calculations/design-conditions";
import { memberPickerArticle } from "./calculations/member-picker";
import { memberResultsArticle } from "./calculations/member-results";
import { steelCfsSheetsArticle } from "./calculations/steel-cfs-sheets";
import { concreteFoundationSheetsArticle } from "./calculations/concrete-foundation-sheets";
import { masonrySheetsArticle } from "./calculations/masonry-sheets";
import { sectionPropertiesArticle } from "./calculations/section-properties";
import { connectionChecksArticle } from "./calculations/connection-checks";
import { fasteningEscalationArticle } from "./calculations/fastening-escalation";
import { projectSetupArticle } from "./projects/project-setup";
import { hazardReportsArticle } from "./projects/hazard-reports";
import { floorPlansArticle } from "./projects/floor-plans";
import { viewerReviewArticle } from "./projects/viewer-review";
import { keyboardShortcutsArticle } from "./reference/keyboard-shortcuts";
import { authoredHelpArticles } from "./authoredArticles";
import { productLookupArticle } from "./calculations/product-lookup";
import { loadCalcsArticle } from "./calculations/load-calcs";
import { loadCalcResultsArticle } from "./calculations/load-calc-results";
import { wallAssemblyArticle } from "./calculations/wall-assembly";
import { linkingReactionsArticle } from "./calculations/linking-reactions";
import { reportDetailsArticle } from "./calculations/report-details";
import { calcExportArticle } from "./calculations/calc-export";

// The phase-A IA used this helper for reserved slots. Every slot is now backed
// by an authored article; retaining the helper keeps this ordered inventory
// compact while failing fast if a future slot is added without content.
function planned(id: string, title: string): HelpArticle {
  const article = authoredHelpArticles[id];
  if (!article) {
    throw new Error(`Help article "${id}" (${title}) has no authored content.`);
  }
  return article;
}

// Publication is an integration concern. Author branches keep modules hidden;
// this one calc-only manifest publishes the reconciled walkthrough corpus.
function publishArticle(article: HelpArticle): HelpArticle {
  return article.published ? article : { ...article, published: true };
}

export const helpTabs: HelpTab[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary: "The shell, the palette, and your first project.",
    sections: [
      {
        id: "orientation",
        title: "Orientation",
        articles: [
          navigatingFirmarkArticle,
          planned("creating-a-design-project", "Creating a design project"),
        ],
      },
    ],
  },
  {
    id: "calculations",
    title: "Calculations",
    summary: "Calculation projects, sheets, loads, results, and export.",
    sections: [
      {
        id: "start-here",
        title: "Start here",
        articles: [publishArticle(yourFirstMemberArticle)],
      },
      {
        id: "projects-and-registers",
        title: "Projects & registers",
        articles: [
          publishArticle(calculationRegisterArticle),
          publishArticle(organizingCalculationsArticle),
          publishArticle(managingCalculationsArticle),
        ],
      },
      {
        id: "sheets",
        title: "Working in sheets",
        articles: [
          publishArticle(memberSheetsArticle),
          publishArticle(schemaSheetsArticle),
          publishArticle(sheetLifecycleArticle),
          publishArticle(designMethodArticle),
        ],
      },
      {
        id: "wood-members",
        title: "Wood members",
        articles: [
          publishArticle(memberRolePresetsArticle),
          publishArticle(memberGeometrySupportsArticle),
          publishArticle(memberLoadsArticle),
          publishArticle(designConditionsArticle),
          publishArticle(memberPickerArticle),
          publishArticle(memberResultsArticle),
        ],
      },
      {
        id: "structural-calculators",
        title: "Steel, concrete, masonry & analysis",
        articles: [
          publishArticle(steelCfsSheetsArticle),
          publishArticle(concreteFoundationSheetsArticle),
          publishArticle(masonrySheetsArticle),
          publishArticle(sectionPropertiesArticle),
        ],
      },
      {
        id: "connections",
        title: "Connections",
        articles: [
          publishArticle(connectionChecksArticle),
          publishArticle(fasteningEscalationArticle),
          publishArticle(productLookupArticle),
        ],
      },
      {
        id: "loads",
        title: "Load calculations & linking",
        articles: [
          publishArticle(loadCalcsArticle),
          publishArticle(loadCalcResultsArticle),
          publishArticle(wallAssemblyArticle),
          publishArticle(linkingReactionsArticle),
        ],
      },
      {
        id: "reports",
        title: "Reports & export",
        articles: [
          publishArticle(reportDetailsArticle),
          publishArticle(calcExportArticle),
        ],
      },
    ],
  },
  {
    id: "projects",
    title: "Design projects",
    summary: "From floor plan to engineered review.",
    sections: [
      {
        id: "pipeline",
        title: "From plan to review",
        articles: [
          projectSetupArticle,
          hazardReportsArticle,
          floorPlansArticle,
          viewerReviewArticle,
        ],
      },
    ],
  },
  {
    id: "reference",
    title: "Reference",
    summary: "Shortcuts and product concepts.",
    sections: [
      {
        id: "shortcuts",
        title: "Shortcuts",
        articles: [keyboardShortcutsArticle],
      },
      {
        id: "concepts",
        title: "Concepts",
        articles: [
          planned("statuses-and-verdicts", "Statuses, DCRs & verdicts"),
          planned("policies-concepts", "Project policies & defaults"),
          planned("materials-db", "The materials database"),
          planned("load-types", "Load types & service-level values"),
          planned("load-combinations", "Load combinations & duration factors"),
          planned("calculation-provenance", "Calculation records, sources & assumptions"),
        ],
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    summary: "Product refusals, failed lookups, stale links, and export preflight.",
    sections: [
      {
        id: "common-problems",
        title: "Common problems",
        articles: [
          planned("calculation-wont-run", "When a calculation will not run"),
          planned("catalog-selection-problems", "Catalog selection problems"),
          planned("reaction-link-problems", "Reaction-link problems"),
          planned("export-problems", "Export problems"),
        ],
      },
    ],
  },
  {
    id: "calculator-reference",
    title: "Calculator reference",
    summary: "Generated field, result, limitation, and source reference from the live registry.",
    sections: [
      {
        id: "wood-reference",
        title: "Wood members",
        articles: [
          planned("calculator-wood-beam-asd", "Wood Beam"),
          planned("calculator-wood-column-asd", "Wood Column"),
          planned("calculator-wood-tension-asd", "Wood Tension / Roof Tie"),
          planned("calculator-wood-stud-wall-asd", "Wood Stud Wall"),
          planned("calculator-wood-scl-beam-asd", "SCL Beam (LVL / LSL / PSL)"),
          planned("calculator-wood-biaxial-beam-column-asd", "Wood Biaxial Beam-Column"),
          planned("calculator-glulam-beam-asd", "Glulam Beam"),
          planned("calculator-wood-ijoist-asd", "Wood I-Joist"),
        ],
      },
      {
        id: "steel-reference",
        title: "Steel & cold-formed steel",
        articles: [
          planned("calculator-steel-beam-aisc", "Steel Beam"),
          planned("calculator-steel-column-aisc", "Steel Column"),
          planned("calculator-cfs-joist", "Cold-Formed Steel Joist"),
          planned("calculator-cfs-stud-wall", "Cold-Formed Steel Stud Wall"),
          planned("calculator-cfs-header", "Cold-Formed Steel Header"),
        ],
      },
      {
        id: "concrete-reference",
        title: "Concrete & foundations",
        articles: [
          planned("calculator-concrete-beam-aci", "Concrete Beam"),
          planned("calculator-continuous-footing", "Strip / Continuous Footing"),
          planned("calculator-pad-footing", "Pad Footing"),
        ],
      },
      {
        id: "masonry-reference",
        title: "Masonry",
        articles: [
          planned("calculator-masonry-bearing-wall-tms", "Masonry Out-of-Plane Wall"),
          planned("calculator-masonry-shear-wall-tms", "Masonry Shear Wall"),
          planned("calculator-masonry-lintel-tms", "Masonry Lintel"),
        ],
      },
      {
        id: "connection-reference",
        title: "Connections",
        articles: [
          planned("calculator-wood-nail-connection-asd", "Wood Nail / Spike Connection"),
          planned("calculator-wood-bolt-connection-asd", "Wood Bolt Connection"),
          planned("calculator-wood-lag-connection-asd", "Wood Lag-Screw Connection"),
          planned("calculator-wood-screw-connection-asd", "Wood-Screw Connection"),
          planned("calculator-connector-product-capacity", "Connector Product Capacity"),
          planned("calculator-proprietary-hanger-connection", "Proprietary Hanger Capacity Check"),
          planned("calculator-proprietary-hold-down-connection", "Proprietary Hold-Down Load Path"),
        ],
      },
      {
        id: "load-reference",
        title: "Loads",
        articles: [
          planned("calculator-load-wind-mwfrs", "Wind — MWFRS"),
          planned("calculator-load-wind-cc", "Wind — Components and Cladding"),
          planned("calculator-load-snow", "Snow — Balanced, Unbalanced, and Drift"),
          planned("calculator-load-seismic-elf", "Seismic — Equivalent Lateral Force"),
          planned("calculator-load-dead-gravity", "Dead and Gravity Loads"),
          planned("calculator-load-roof-live", "Roof Live Load"),
          planned("calculator-load-floor-deck-live", "Floor and Deck Live Loads"),
          planned("calculator-load-ceiling-attic-live", "Ceiling and Attic Live Loads"),
          planned("calculator-load-rain-ice", "Rain and Atmospheric Ice Loads"),
          planned("calculator-load-wall-gravity", "Wall Gravity Loads"),
        ],
      },
      {
        id: "analysis-reference",
        title: "Analysis",
        articles: [planned("calculator-section-properties", "Section Properties")],
      },
    ],
  },
];

// ---- Published projections (what navigation and search actually see) -------

export const publishedHelpTabs: HelpTab[] = helpTabs
  .map((tab) => ({
    ...tab,
    sections: tab.sections
      .map((section) => ({
        ...section,
        articles: section.articles.filter((article) => article.published),
      }))
      .filter((section) => section.articles.length > 0),
  }))
  .filter((tab) => tab.sections.length > 0);

export type HelpTopicHit = {
  tab: HelpTab;
  section: HelpSection;
  article: HelpArticle;
};

/** Resolve a topic id to its published article (unpublished ids resolve to
 *  null — a reserved slot must never render through a deep link either). */
export function findHelpTopic(topicId: string): HelpTopicHit | null {
  for (const tab of helpTabs) {
    for (const section of tab.sections) {
      for (const article of section.articles) {
        if (article.id === topicId) {
          return article.published ? { tab, section, article } : null;
        }
      }
    }
  }
  return null;
}

/** Full-page route for a published topic id. */
export function helpArticleRoute(topicId: string): string | null {
  const hit = findHelpTopic(topicId);
  return hit ? `/app/help/${hit.tab.id}/${hit.article.id}` : null;
}

// Guard against id collisions at module load (dev-time safety; the manifest is
// static so this can't fire in production unless a duplicate is committed).
if (import.meta.env?.DEV) {
  const seen = new Set<string>();
  for (const tab of helpTabs) {
    for (const section of tab.sections) {
      for (const article of section.articles) {
        if (seen.has(article.id)) {
          throw new Error(`Duplicate help topic id: ${article.id}`);
        }
        seen.add(article.id);
      }
    }
  }
}
